import { useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  type Cell,
  type PaginationState,
  type SortingState,
  type ColumnFiltersState,
} from '@tanstack/react-table';
import { 
  ChevronDown, 
  ChevronUp, 
  Download, 
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import type { TableData, TableRow, TableColumn } from '@/types';
import { cn, exportToCSV } from '@/lib/utils';
import { embeddedTableWeb, embeddedWeb } from './embeddedDesign';

interface EmbeddedTableProps {
  tableData: TableData;
}

export function EmbeddedTable({ tableData }: EmbeddedTableProps) {
  const { t } = useTranslation();
  const safeTableData = {
    ...tableData,
    title: String(tableData?.title || t('embedded.table.defaultTitle', 'Results')),
    columns: Array.isArray(tableData?.columns) ? tableData.columns : [],
    rows: Array.isArray(tableData?.rows) ? tableData.rows : [],
  };
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: embeddedTableWeb.defaultPageSize,
  });

  // Convert table columns to TanStack Table format
  const columns = useMemo(
    () =>
      safeTableData.columns.map((col: TableColumn) => ({
        id: col.id,
        header: col.header,
        accessorKey: col.accessorKey,
        enableSorting: col.sortable ?? safeTableData.enableSorting ?? true,
        enableColumnFilter: col.filterable ?? safeTableData.enableFiltering ?? false,
        cell: col.cell
          ? ({ row, getValue }: any) => col.cell!(getValue(), row.original)
          : undefined,
        size: col.width ? parseInt(col.width) : undefined,
      })),
    [safeTableData.columns, safeTableData.enableSorting, safeTableData.enableFiltering]
  );

  const table = useReactTable({
    data: safeTableData.rows,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      pagination,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableSorting: safeTableData.enableSorting ?? true,
    enableFilters: safeTableData.enableFiltering ?? true,
    enableGlobalFilter: safeTableData.enableFiltering ?? true,
  });

  const handleExport = () => {
    const data = table.getFilteredRowModel().rows.map((row) => row.original);
    exportToCSV(data, `${safeTableData.title.replace(/\s+/g, '_')}.csv`);
  };

  const handleRowClick = (row: TableRow) => {
    if (safeTableData.onRowClick) {
      safeTableData.onRowClick(row);
    }
  };

  const renderCellValue = (cell: Cell<TableRow, unknown>) => {
    if (cell.column.columnDef.cell) {
      return flexRender(cell.column.columnDef.cell, cell.getContext());
    }

    const value = cell.getValue();
    if (value === null || value === undefined || value === '') {
      return <span className="text-[var(--host-text-secondary)]">-</span>;
    }
    if (typeof value === 'boolean') {
      return value ? t('common.yes', 'Yes') : t('common.no', 'No');
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  };

  return (
    <div className={embeddedWeb.container}>
      {/* Header */}
      <div className={embeddedWeb.header}>
        <div className={embeddedWeb.headerRow}>
          <h3 className={embeddedWeb.title}>
            {safeTableData.title}
          </h3>

          <div className={embeddedTableWeb.toolbar}>
            {/* Global search */}
            {safeTableData.enableFiltering && (
              <div className={embeddedTableWeb.searchWrap}>
                <Search className={embeddedWeb.searchIcon} />
                <input
                  type="text"
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  placeholder={t('embedded.search.placeholder', 'Search...')}
                  aria-label={`${t('embedded.search.ariaPrefix', 'Search')} ${safeTableData.title}`}
                  className={embeddedWeb.input}
                />
              </div>
            )}

            {/* Export button */}
            {safeTableData.enableExport && (
              <button
                onClick={handleExport}
                className={embeddedTableWeb.exportButton}
                type="button"
              >
                <Download className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{t('common.export', 'Export')}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table - Desktop */}
      <div className={embeddedTableWeb.desktopWrap}>
        <table className="w-full">
          <thead className={embeddedTableWeb.head}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={cn(
                      embeddedTableWeb.th,
                      header.column.getCanSort() && 'cursor-pointer select-none hover:bg-primary-50'
                    )}
                    onClick={header.column.getToggleSortingHandler()}
                    style={{ width: header.getSize() }}
                  >
                    <div className={embeddedTableWeb.thInner}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <span className={embeddedTableWeb.sortIcon}>
                          {header.column.getIsSorted() === 'asc' ? (
                            <ChevronUp className="w-3.5 h-3.5" />
                          ) : header.column.getIsSorted() === 'desc' ? (
                            <ChevronDown className="w-3.5 h-3.5" />
                          ) : (
                            <div className={embeddedTableWeb.sortPlaceholder} />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
                {safeTableData.rowActions && safeTableData.rowActions.length > 0 && (
                  <th className={embeddedTableWeb.thAction}>
                    {t('common.actions', 'Actions')}
                  </th>
                )}
              </tr>
            ))}
          </thead>
          <tbody className={embeddedTableWeb.body}>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={() => handleRowClick(row.original)}
                className={cn(
                  embeddedTableWeb.row,
                  safeTableData.onRowClick && embeddedTableWeb.rowClickable
                )}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={embeddedTableWeb.td}>
                    {renderCellValue(cell)}
                  </td>
                ))}
                {safeTableData.rowActions && safeTableData.rowActions.length > 0 && (
                  <td className={embeddedTableWeb.actionCell}>
                    <div className={embeddedTableWeb.actionWrap}>
                      {safeTableData.rowActions.map((action) => (
                        <button
                          key={action.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            action.onClick(row.original);
                          }}
                          disabled={action.disabled?.(row.original)}
                          type="button"
                          className={cn(
                            embeddedTableWeb.actionButton,
                            action.variant === 'danger' &&
                              embeddedTableWeb.actionDanger,
                            action.variant === 'success' &&
                              embeddedTableWeb.actionSuccess,
                            (!action.variant || action.variant === 'default') &&
                              embeddedTableWeb.actionDefault
                          )}
                        >
                          {action.label}
                        </button>
                      ))}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Cards - Mobile */}
      <div className={embeddedTableWeb.mobileWrap}>
        {table.getRowModel().rows.map((row) => (
          <div
            key={row.id}
            onClick={() => handleRowClick(row.original)}
            className={cn(
              embeddedTableWeb.mobileRow,
              safeTableData.onRowClick && 'active:bg-primary-50'
            )}
          >
            {row.getVisibleCells().map((cell) => (
              <div key={cell.id} className={embeddedTableWeb.mobileField}>
                <span className={embeddedTableWeb.mobileLabel}>
                  {cell.column.columnDef.header as string}
                </span>
                <span className={embeddedTableWeb.mobileValue}>
                  {renderCellValue(cell)}
                </span>
              </div>
            ))}
            {safeTableData.rowActions && safeTableData.rowActions.length > 0 && (
              <div className={embeddedTableWeb.mobileActions}>
                {safeTableData.rowActions.map((action) => (
                  <button
                    key={action.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      action.onClick(row.original);
                    }}
                    disabled={action.disabled?.(row.original)}
                    type="button"
                    className={cn(
                      embeddedTableWeb.mobileActionButton,
                      action.variant === 'danger' &&
                        embeddedTableWeb.mobileActionDanger,
                      action.variant === 'success' &&
                        embeddedTableWeb.mobileActionSuccess,
                      (!action.variant || action.variant === 'default') &&
                        embeddedTableWeb.mobileActionDefault
                    )}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pagination */}
      {safeTableData.enablePagination && (
        <div className={embeddedTableWeb.pagination}>
          <div className={embeddedTableWeb.paginationInner}>
            {/* Page info */}
            <div className={embeddedTableWeb.paginationText}>
              {t('embedded.table.showing', 'Showing')}{' '}
              {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} {t('common.to', 'to')}{' '}
              {Math.min(
                (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                table.getFilteredRowModel().rows.length
              )}{' '}
              {t('common.of', 'of')} {table.getFilteredRowModel().rows.length} {t('embedded.table.results', 'results')}
            </div>

            {/* Page controls */}
            <div className={embeddedTableWeb.paginationControls}>
              <button
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
                className={embeddedTableWeb.paginationButton}
                type="button"
                aria-label={t('embedded.table.firstPage', 'First page')}
              >
                <ChevronsLeft className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className={embeddedTableWeb.paginationButton}
                type="button"
                aria-label={t('embedded.table.previousPage', 'Previous page')}
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              
              <span className={embeddedTableWeb.pageCurrentText}>
                {t('embedded.table.page', 'Page')} {table.getState().pagination.pageIndex + 1}{' '}
                {t('common.of', 'of')} {table.getPageCount()}
              </span>
              
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className={embeddedTableWeb.paginationButton}
                type="button"
                aria-label={t('embedded.table.nextPage', 'Next page')}
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
                className={embeddedTableWeb.paginationButton}
                type="button"
                aria-label={t('embedded.table.lastPage', 'Last page')}
              >
                <ChevronsRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {table.getRowModel().rows.length === 0 && (
        <div className={embeddedWeb.empty}>
          <p className={embeddedWeb.emptyTitle}>
            {t('embedded.empty.noMatchingRecords', 'No matching records')}
          </p>
          <p className={embeddedWeb.emptyText}>
            {t('embedded.empty.adjustSearchOrFilters', 'Adjust the search or filters and try again.')}
          </p>
        </div>
      )}
    </div>
  );
}
